import { PaymentEntryPayload } from '../shared/interfaces/payment.interface';
import { centsToDecimalString } from '../utils/strings';


export interface CardInput {
  number: string;
  holder: string;
  document: string;
  expiryMonth: string;
  expiryYear: string;
  cvc: string;
}

interface PagarmeTokenResponse {
  id: string;
}

interface PagarmeErrorResponse {
  message?: string;
  errors?: Record<string, string[]> | Array<{ message?: string }>;
}

const CARD_ERROR_DICTIONARY: Array<{ pattern: RegExp; label: string }> = [
  { pattern: /not a valid card number|invalid card number/i, label: 'inválido' },
  { pattern: /card expired|expired/i, label: 'expirado' },
  { pattern: /cvv/i, label: 'com código de segurança (CVV) incorreto' },
  { pattern: /holder[_ ]?name/i, label: 'com nome do titular inválido' },
  { pattern: /holder[_ ]?document/i, label: 'com CPF/CNPJ do titular inválido' },
  { pattern: /exp[_ ]?month|exp[_ ]?year/i, label: 'com data de validade inválida' },
  { pattern: /declined|refused/i, label: 'recusado pela operadora' },
  { pattern: /insufficient|limit/i, label: 'sem limite disponível' },
];

const GENERIC_CARD_ERROR_MESSAGE = 'Não foi possível validar o cartão. Verifique os dados e tente novamente.';

function collectRawMessages(errors: PagarmeErrorResponse['errors']): string[] {
  if (!errors) return [];

  if (Array.isArray(errors)) {
    return errors
      .map((e) => e.message)
      .filter((m): m is string => Boolean(m));
  }
  return Object.values(errors)
    .flat()
    .filter((m): m is string => Boolean(m));
}

function translateCardErrors(rawMessages: string[]): string | null {
  const labels = new Set<string>();

  rawMessages.forEach((raw) => {
    const match = CARD_ERROR_DICTIONARY.find((entry) => entry.pattern.test(raw));
    if (match) labels.add(match.label);
  });

  if (labels.size === 0) return null;

  return `Cartão ${Array.from(labels).join(' ou ')}.`;
}

function extractErrorMessage(body: PagarmeErrorResponse | null): string {
  if (!body) return GENERIC_CARD_ERROR_MESSAGE;

  const rawMessages = collectRawMessages(body.errors);
  const translated = translateCardErrors(rawMessages);

  return translated || GENERIC_CARD_ERROR_MESSAGE;
}

export async function tokenizeCard(card: CardInput): Promise<string> {
  const publicKey = process.env.REACT_APP_PAGAR_ME_PUBLIC_KEY;

  if (!publicKey) {
    throw new Error('Chave pública da Pagar.me não configurada.');
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), Number(process.env.REACT_APP_PAGAR_ME_TOKENIZE_TIMEOUT_MS));

  let response: Response;

  try {
    response = await fetch(`${process.env.REACT_APP_PAGAR_ME_TOKENS_URL}?appId=${publicKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({
        type: 'card',
        card: {
          number: card.number.replace(/\s/g, ''),
          holder_name: card.holder,
          holder_document: card.document.replace(/\D/g, ''),
          exp_month: Number(card.expiryMonth),
          exp_year: Number(card.expiryYear),
          cvv: card.cvc,
        },
      }),
    });
  } catch (err: any) {
    if (err.name === 'AbortError') {
      throw new Error('Tempo esgotado ao tokenizar o cartão. Tente novamente.');
    }
    throw new Error('Não foi possível conectar à Pagar.me. Verifique sua conexão.');
  } finally {
    clearTimeout(timeout);
  }

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(extractErrorMessage(body));
  }

  return (body as PagarmeTokenResponse).id;
}

export interface PaymentMethodsFormSubmitData {
  method: 'credit_card' | 'boleto' | 'two_cards' | 'card_boleto';
  amount: number; // centavos
  cardData?: CardInput;
  secondCardData?: CardInput;
  installments?: number;
  secondInstallments?: number;
  firstPaymentAmount?: number; // centavos
}

function requireCardData(card: CardInput | undefined, label: string): CardInput {
  if (!card) {
    throw new Error(`Dados do ${label} ausentes.`);
  }
  return card;
}

function requireAmount(amount: number | undefined, label: string): number {
  if (amount === undefined) {
    throw new Error(`${label} ausente.`);
  }
  return amount;
}

export async function buildPaymentsPayload(
  data: PaymentMethodsFormSubmitData,
): Promise<PaymentEntryPayload[]> {
  const payments: PaymentEntryPayload[] = [];

  if (data.method === 'credit_card') {
    const card = requireCardData(data.cardData, 'cartão');
    const cardToken = await tokenizeCard(card);
    payments.push({
      method: 'credit_card',
      amount: centsToDecimalString(data.amount),
      cardToken,
      installments: data.installments,
    });
  } else if (data.method === 'boleto') {
    payments.push({
      method: 'boleto',
      amount: centsToDecimalString(data.amount),
    });
  } else if (data.method === 'two_cards') {
    const card1 = requireCardData(data.cardData, '1º cartão');
    const card2 = requireCardData(data.secondCardData, '2º cartão');
    const [token1, token2] = await Promise.all([
      tokenizeCard(card1),
      tokenizeCard(card2),
    ]);
    const firstCents = Math.round(requireAmount(data.firstPaymentAmount, 'valor do split'));
    const secondCents = Math.round(data.amount) - firstCents;
    payments.push({
      method: 'credit_card',
      amount: centsToDecimalString(firstCents),
      cardToken: token1,
      installments: data.installments,
    });
    payments.push({
      method: 'credit_card',
      amount: centsToDecimalString(secondCents),
      cardToken: token2,
      installments: data.secondInstallments,
    });
  } else if (data.method === 'card_boleto') {
    const card = requireCardData(data.cardData, 'cartão');
    const cardToken = await tokenizeCard(card);
    const firstCents = Math.round(requireAmount(data.firstPaymentAmount, 'valor do split'));
    const secondCents = Math.round(data.amount) - firstCents;
    payments.push({
      method: 'credit_card',
      amount: centsToDecimalString(firstCents),
      cardToken,
      installments: data.installments,
    });
    payments.push({
      method: 'boleto',
      amount: centsToDecimalString(secondCents),
    });
  }

  return payments;
}

const ALREADY_PAID_KEYWORDS = [
  'já foi pago',
  'já pago',
  'pagamento já processado',
  'pagamento existente',
];

export function isAlreadyPaidMessage(message: string | undefined | null): boolean {
  if (!message) return false;
  const normalized = message.toLowerCase();
  return ALREADY_PAID_KEYWORDS.some((keyword) => normalized.includes(keyword));
}
